import React, { useContext, useMemo, useState } from 'react';
import {
  FormGroup,
  Button,
  NonIdealState,
  Label,
  MenuItem,
  Spinner,
} from '@blueprintjs/core';
import {
  useClients,
  useClientEmail,
} from '@axo/shared/data-access/hooks';
import { useAgents } from '../../hooks/queries/useAgents';
import { ace_hooks } from '@axo/shared/data-access/hooks';
import { AssignmentType } from '@axo/shared/data-access/types/ace';
import { DataAccessContext } from '@axo/shared/data-access/provider';
import { getLocalPartOfEmail } from '../../utils/getLocalPartOfEmail';
import { AppToaster } from '../AppToaster';
import styles from './TransferApplicationDialog.module.css';
import { Select } from '@blueprintjs/select';

export interface DialogContentProps {
  onClose: () => void;
  applicationId: string;
  currentOwnerId?: string;
  marketCountry: string;
  assignmentType: string;
  onLoadingChange: (loading: boolean) => void;
}

export const DialogContent: React.FC<DialogContentProps> = ({
  onClose,
  applicationId,
  currentOwnerId,
  marketCountry,
  assignmentType,
  onLoadingChange,
}) => {
  const [selectedOption, setSelectedOption] = useState<{
    label: string;
    value: string;
  } | null>(null);
  const { state } = useContext(DataAccessContext);
  const currentClientID = state.user.clientID;
  const currentUserEmail = useClientEmail(currentClientID);
  const senderInitials = currentUserEmail.data
    ? getLocalPartOfEmail(currentUserEmail.data)
    : undefined;

  const agents = useAgents({
    marketCountry,
    assignmentType: (assignmentType as AssignmentType) || undefined,
  });
  const clients = useClients();
  const transferMutation = ace_hooks.useTransferApplication();

  const handleTransfer = async () => {
    if (!selectedOption) return;

    onLoadingChange(true);
    try {
      await transferMutation.mutateAsync({
        applicationId,
        newAssigneeId: selectedOption.value,
        assignmentType: assignmentType as AssignmentType | '',
        marketCountry,
        senderInitials,
      });
      AppToaster.then((t) =>
        t.show({ intent: 'success', message: 'Application transferred successfully' })
      );
      onClose();
    } catch (error) {
      AppToaster.then((t) =>
        t.show({
          intent: 'danger',
          message: ace_hooks.getErrorMessage(
            error as { response?: { status?: number }; status?: number }
          ),
        })
      );
    } finally {
      onLoadingChange(false);
    }
  };

  const selectOptions = useMemo(() => {
    if (!agents.data?.Items || !clients.data?.Items) return [];

    const clientMap = new Map(clients.data.Items.map((c) => [c.ID, c.Email]));

    return agents.data.Items.filter(
      (agent) => agent.ClientID !== currentOwnerId
    )
      .map((agent) => {
        const email = clientMap.get(agent.ClientID) ?? agent.ClientID;
        return {
          label: `${getLocalPartOfEmail(email)} (${email})`,
          value: agent.ClientID,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [agents.data?.Items, clients.data?.Items, currentOwnerId]);

  const isLoading = agents.isLoading || clients.isLoading;
  const hasAgents = selectOptions.length > 0;

  return (
    <>
      <div className={`bp4-dialog-body ${styles.dialogBody}`}>
        {isLoading ? (
          <Spinner size={30} />
        ) : hasAgents ? (
          <FormGroup
            label="You are about to transfer ownership of this application to another operator."
            labelFor="agent-select"
          >
            <Label htmlFor="agent-select">Choose operator:</Label>
            <Select
              fill
              items={selectOptions}
              activeItem={selectedOption}
              itemRenderer={(item, { handleClick, handleFocus }) => (
                <MenuItem
                  key={item.value}
                  text={item.label}
                  onClick={handleClick}
                  onFocus={handleFocus}
                  selected={item.value === selectedOption?.value}
                />
              )}
              itemPredicate={(query, item) =>
                item.label.toLowerCase().includes(query.toLowerCase())
              }
              onItemSelect={setSelectedOption}
              disabled={transferMutation.isLoading}
              filterable={true}
              noResults={<MenuItem disabled text="No agents found" />}
            >
              <Button
                fill
                alignText="left"
                size="small"
                text={selectedOption?.label ?? 'Select an operator...'}
                endIcon="caret-down"
                disabled={transferMutation.isLoading}
              />
            </Select>
          </FormGroup>
        ) : (
          <NonIdealState
            icon="inbox"
            title="No eligible agents"
            description="All agents in this market are currently unavailable. Contact a manager for assistance."
          />
        )}
      </div>

      <div className={styles.dialogFooter}>
        {hasAgents && (
          <Button
            intent="primary"
            onClick={handleTransfer}
            disabled={!selectedOption || transferMutation.isLoading}
            loading={transferMutation.isLoading}
          >
            Transfer
          </Button>
        )}
      </div>
    </>
  );
};
